// App ////////////////////////////////////////////////////////////////////////
//
// This is the top-level file for the search directory.
///////////////////////////////////////////////////////////////////////////////

// SETUP //////////////////////////////////////////////////////////////////////
// Third-party imports.
import { BrowserRouter as Router, Route, } from 'react-router-dom'
import { Container, Row, Col, } from 'reactstrap'
import { useJsApiLoader, } from '@react-google-maps/api'
import React, { useState, Suspense, } from 'react'

// Local imports.
import { arrayIsNullOrEmpty } from './util/helpers'
import { searchAppActions, useSearchAppReducer, } from './containers/SearchAppReducer'
import DetailContainer from './containers/DetailContainer'
import ErrorBoundary from './components/ErrorBoundary'
import QueryRouter from './approuter'
import useLookupFetch from './hooks/useLookupFetch'

// Local imports - lazy loading.
const ESContainer = React.lazy(() => import('./containers/ESContainer'))
const ESDetailWrapper = React.lazy(() => import('./containers/ESDetailContainer'))
const PortalContainer = React.lazy(() => import('./containers/ProgramPortalContainer'))
const ProgramFormContainer = React.lazy(() => import('./containers/ProgramFormContainer'))
const SearchContainer = React.lazy(() => import('./containers/SearchContainer'))

// HELPER FUNCTIONS /////////////////////////////////////////////////////////// 
/**
 * Function for fetching the current geolocation.
 * 
 * @param {*} success 
 * @param {*} failure 
 */
function getLocation(success, failure) {
  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(success)
  } else {
    failure()
  }
}

/**
 * Filter a search result record to those that include a specific category.
 * 
 * @param {*} input 
 * @param {string} c 
 * @returns 
 */
function filterRecord(input, c) {
  if (!arrayIsNullOrEmpty(input.categories) && input.categories.includes(c))
    return true
  else
    return false
}

/**
 * Filter the input, returning it unchanged if no filtering is required.
 * 
 * @param {Array} input 
 * @param {string} filterCategory 
 * @returns 
 */
function filterResults(input, filterCategory) {
  const filtered = filterCategory !== 'all'
  const resultsAreEmpty = arrayIsNullOrEmpty(input)

  if (resultsAreEmpty)
    return []

  else if (filtered)
    return input.filter((r) => filterRecord(r, filterCategory))

  else if (!filtered)
    return input

  else {
    console.error('An issue occurred while filtering results.')
    return input
  } 
}

/**
 * Function for setting the environment name.
 * 
 * @returns 
 */
const EnvName = () => {
  if (process.env.NODE_ENV !== 'production') {
    const markup = []
    let envName = process.env.NODE_ENV
    if (envName === 'testing') {
      envName = 'test'
    }
    for (let i = markup.length, len = 30; i < len; i++) {
      const key = i
      markup.push(<p className={'red-text d-inline-block mx-4'} key={key} aria-hidden={'true'} tabIndex={-1}>{envName}</p>)
    }
    return <div className={'position-fixed mt-1'} aria-hidden={'true'} tabIndex={-1} style={{ width: '9999em', pointerEvents: 'none', zIndex: 10001, }}>{markup}</div>
  }
  return null
}
const EnvNameMemo = React.memo(EnvName)

// MAIN FUNCTION //////////////////////////////////////////////////////////////
function App() {
  const [searchResults, setSearchResults,] = useState([])
  const [searched, setSearched,] = useState(false)
  const [searchFilters, setSearchFilters,] = useState({})
  const [activeProgramType, setActiveProgramType,] = useState('all')
  const [zipCode, setZipCode,] = useState('')
  const [emptyMessage, setEmptyMessage,] = useState('')
  const [userLocation, setUserLocation,] = useState(null)
  const [countiesLoading, counties,] = useLookupFetch('Counties')
  const [programTypesLoading, programTypes,] = useLookupFetch('ProgramTypes')
  const [, programLevels,] = useLookupFetch('ProgramLevels')
  const [, eligReqs,] = useLookupFetch('EligibilityRequirements')
  const [minEduReqsLoading, minEduReqs,] = useLookupFetch('MinimumEducationRequirements')
  const [areasOfInterestLoading, areasOfInterest,] = useLookupFetch('AreasOfInterest')
  const esDataRef = React.useRef(null)
  const [searchState, searchDispatch,] = useSearchAppReducer()
  const {
    activeDetailProgram,
    returnMapState,
    returnLocationFilter,
  } = searchState

  // Set the environment variable.
  let envName = null
  if (process.env.NODE_ENV !== 'production') {
    envName = <EnvNameMemo />
  }

  // Logic for updating the Google Map.
  const { isLoaded: mapLibLoaded, loadError: mapLibError, } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GMAP_API_KEY,
  })

  // CALLBACK FUNCTIONS ///////////////////////////////////////////////////////
  // Do a location request.
  const requestLocation = React.useCallback(() => {
    const success = (location) => {
      setUserLocation(location)
    }
    const fail = () => {
      console.error('Unable to retrieve user location')
    }
    getLocation(success, fail)
  }, [])

  // Logic for updating search results.
  const searchFiltersChanged = React.useCallback((filters) => {
    if (filters)
      setSearchFilters(filters)
  }, [setSearchFilters,])

  const resetSearchReturnData = React.useCallback(() => {
    searchDispatch(searchAppActions.resetSearchReturnData())
  }, [searchDispatch,])

  const searchResultsChanged = React.useCallback((searchResults) => {
    setSearchResults(searchResults)
  }, [])

  // Function to update the active program when a program is selected from the sidebar or map pin.
  const activateDetailProgram = React.useCallback((id, type, program, mapState, locationFilterState) => {
    // Clear out the active detail program.
    searchDispatch(searchAppActions.setActiveDetailProgram(null))

    // Look through all possible programs for a match, activating if found
    const matches = searchResults.filter(p => p.program_occupation_id === id && p.type === type)
    const prg = (matches && matches.length > 0) ? matches.at(0) : null
    if (prg) 
      searchDispatch(searchAppActions.activateDetailProgram(prg, mapState, locationFilterState))

    // Otherwise, complain to the console.
    else { 
      console.error(`ActivateDetailProgram: No program with ${id} and ${type} was found.`)
    }
  }, [ searchDispatch, searchResults, ])

  // Function to pull in data for the active program when a program is selected from the sidebar or map pin.
  const setActiveDetailProgram = React.useCallback((program) => {
    searchDispatch(searchAppActions.setActiveDetailProgram(program))
  }, [ searchDispatch, ])

  // SPECIAL //////////////////////////////////////////////////////////////////
  const resultsToRender = React.useMemo(() => {
    const results = filterResults(searchResults, activeProgramType)
    if (arrayIsNullOrEmpty(results))
      setEmptyMessage('No results found. Please try a different search.')
    return results
  }, [activeProgramType, searchResults,])

  // RETURN HTML ///////////////////////////////////////////////////////////////
  return (
    <Router>
      <Route path={['/', '/index.html',]}>
        {envName}
        <QueryRouter
          detailComponent={
            <Container fluid>
              <Row>
                <Col xs={12}>
                  <ErrorBoundary>
                    <DetailContainer
                      setActiveProgram={setActiveDetailProgram}
                      mapLibReady={mapLibLoaded}
                      program={activeDetailProgram}
                      zipcode={zipCode}
                      userLocation={userLocation}
                      getUserLocation={requestLocation}
                      parentUrl={window.parentUrl}
                    />
                  </ErrorBoundary>
                </Col>
              </Row>
            </Container>
          }

          searchComponent={
            <Container className={'search-container'} fluid>
              <Row>
                <Suspense fallback={<div>Loading...</div>}>
                  <SearchContainer
                    searched={searched}
                    setSearched={setSearched}
                    searchResults={resultsToRender}
                    setResults={searchResultsChanged}

                    activateProgram={activateDetailProgram}
                    activeProgramType={activeProgramType}
                    setActiveProgramType={setActiveProgramType}

                    searchFilters={searchFilters}
                    setSearchFilters={searchFiltersChanged}
                    userLocation={userLocation}
                    getUserLocation={requestLocation}
                    setZipCode={setZipCode}

                    counties={counties}
                    programTypes={programTypes}
                    programLevels={programLevels}
                    eligibilityRequirements={eligReqs}
                    minimumEducationRequirements={minEduReqs}
                    industryTags={areasOfInterest}

                    mapLibLoaded={mapLibLoaded}
                    mapLibLoadError={mapLibError}
                    emptyMessage={emptyMessage}

                    returnMapState={returnMapState}
                    returnLocationFilter={returnLocationFilter}
                    resetSearchReturnData={resetSearchReturnData}
                  />
                </Suspense>
              </Row>
            </Container>
          }

          equityComponent={<Suspense fallback={<div>Loading...</div>}>
            <ESContainer
              mapLibLoaded={mapLibLoaded}
              mapLibLoadError={mapLibError}
              userLocation={userLocation}
              getUserLocation={requestLocation}
              parentRef={esDataRef}
              setZipCode={setZipCode}
              zipCode={zipCode}
            />
          </Suspense>}

          equityDetailComponent={<Suspense fallback={<div>Loading...</div>}>
            <ESDetailWrapper />
          </Suspense>}

          formComponent={<Suspense fallback={<div>Loading...</div>}>
            <ProgramFormContainer
              countiesLoading={countiesLoading}
              counties={counties}
              programTypesLoading={programTypesLoading}
              programTypes={programTypes}
              minEduReqsLoading={minEduReqsLoading}
              userLocation={userLocation}
              minEduReqs={minEduReqs}
              areasOfInterestLoading={areasOfInterestLoading}
              areasOfInterest={areasOfInterest}
            />
          </Suspense>}
          portalComponent={PortalContainer}
        />
      </Route>
    </Router>
  )
}

export default App